import {
  ChangeEvent,
  ClipboardEvent,
  JSX,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import "styles/components/forms/inputs/otpInput.css";

interface OtpInputProps {
  /**
   * Called whenever the user updates the OTP (typing, backspace, paste).
   * Returns the current OTP value and a boolean indicating if it's complete.
   */
  onChange?: (otpValue: string, isComplete: boolean) => void;

  /**
   * Called once the OTP is fully filled with 6 digits (all numeric).
   */
  onComplete?: (otpValue: string) => void;
}

export default function OtpInput({
  onChange,
  onComplete,
}: OtpInputProps): JSX.Element {
  // We store the 6 OTP digits in an array of 6 strings.
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);

  // Create refs to easily focus each input programmatically.
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // A hidden input to capture mobile OTP autofill
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Helper to check if OTP is complete and invoke callbacks.
   */
  const handleOtpUpdate = (newOtp: string[]) => {
    const joined = newOtp.join("");
    const isComplete = joined.length === 6 && /^\d{6}$/.test(joined);

    if (onChange) {
      onChange(joined, isComplete);
    }

    if (isComplete && onComplete) {
      onComplete(joined);
    }
  };

  /**
   * Distribute a single string of digits (up to length 6) into the otp array
   */
  const fillOtp = (digitsString: string) => {
    const digits = digitsString.replace(/\D/g, "").slice(0, 6).split("");
    const newOtp = Array(6).fill("");

    digits.forEach((digit, i) => {
      newOtp[i] = digit;
    });
    setOtp(newOtp);

    // Focus the next empty field or the last one if all are filled
    const nextPos = digits.length < 6 ? digits.length : 5;
    inputRefs.current[nextPos]?.focus();

    handleOtpUpdate(newOtp);
  };

  // Handle input changes in the 6 visible boxes
  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.target;
    // Allow only digits 0-9 (and ignore other input)
    if (!/^\d?$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // If a digit was entered, move to the next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    handleOtpUpdate(newOtp);
  };

  // Handle keyboard events (backspace, arrow keys, etc.)
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      const newOtp = [...otp];

      // If current field is empty, go back one field
      if (!otp[index] && index > 0) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
        e.preventDefault();
      } else {
        // If there's a digit in current field, clear it but stay here
        newOtp[index] = "";
        setOtp(newOtp);
      }
      handleOtpUpdate(newOtp);
    }
  };

  // Handle normal paste (Ctrl+V or long-press paste on mobile)
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    fillOtp(pasteData);
  };

  /**
   * If the mobile keyboard suggests an OTP code, tapping that suggestion
   * typically pastes it into an input with autocomplete="one-time-code" or "sms-otp".
   * We'll store that code and then distribute it into the 6 separate boxes.
   */
  const handleHiddenInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    if (!text) return;
    fillOtp(text);

    // Clear the hidden input to allow re-pasting if needed
    e.target.value = "";
  };

  // Focus hidden input on mount to improve chance of autofill
  useEffect(() => {
    hiddenInputRef.current?.focus();
  }, []);

  return (
    <div>
      {/* Hidden input for mobile OTP autofill */}
      <input
        ref={hiddenInputRef}
        type="text"
        autoComplete="one-time-code" // iOS keyword
        // autoComplete="sms-otp"     // some Android versions
        inputMode="numeric"
        pattern="[0-9]*"
        onChange={handleHiddenInputChange}
        style={{
          position: "absolute",
          left: -9999,
          opacity: 0,
          pointerEvents: "none",
          // Some iOS versions require the input to be "visible" to show OTP prompt.
          // If so, you could simply do: style={{ width: 0, height: 0 }} or a tiny offset.
        }}
      />

      <div className="otp-container">
        {otp.map((digit, index) => (
          <input
            key={index}
            type="text"
            value={digit}
            id={`otpInput-${index + 1}`}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            maxLength={1}
            ref={(el: HTMLInputElement | null) => {
              inputRefs.current[index] = el;
            }}
            className="otp-input"
          />
        ))}
      </div>
    </div>
  );
}
